import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Button, Grid, GridItem, Small, Text } from "orcs-design-system";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import themeGet from "@styled-system/theme-get";
import { map, times } from "lodash";
import { NODE_TYPES } from "src/components/NodeVisualizer/consts";
import { collectionNodeDataPropType } from "src/components/NodeVisualizer/Nodes/propTypes";
import EventBus, { EVENTS } from "src/services/eventEmitter";
import { MAX_ITEMS_PER_PAGE } from "src/components/OrgChart/consts";
import { useOrgChartContext } from "src/components/OrgChart/context/OrgChartContext";
import {
  EDGE_COLOR,
  getVisibilityStyles,
  HIGHLIGHTED_ANIMATION,
  TargetHandle,
} from "../node.styled";
import PersonNode from "../PersonNode";
import GroupNode from "../GroupNode";
import CreatePersonNode from "../CreatePersonNode";
import { RoundIconButton } from "../RoundIconButton";
import { getCollectionGridDimensions } from "./collectionGridDimensions";

export const COLLECTION_NODE_DISPLAY_MODE = {
  SINGLE_COLUMN: "single-column",
  SUB_COLLECTIONS: "sub-collections",
};

/** HELP-10386: avoid stretching tiny member lists to the spacious max width */
const MIN_MEMBER_ITEMS_FOR_WIDE_GRID = 4;

const PositionedPlusButton = styled(RoundIconButton)`
  position: absolute;
  bottom: -11px;
`;

const PositionedLoadButton = styled(Button)`
  position: absolute;
  bottom: 16px;
`;

const PositionedLoadText = styled(Text)`
  position: absolute;
  bottom: 16px;
  padding-bottom: 2px;
`;

const StyledLabel = styled(Small)((props) => {
  const { $labelColor = EDGE_COLOR, $labelRects = {} } = props;
  const { height } = $labelRects;

  return css`
    position: absolute;
    z-index: 1;
    top: -${height / 2 + 5}px;
    background: white;
    color: ${$labelColor};
    text-align: center;
    max-width: 80%;
  `;
});

const StyledBox = styled(Box)((props) => {
  const {
    $borderStyle = `2px solid ${EDGE_COLOR}`,
    isSubCollection,
    $wideMemberGrid,
  } = props;

  return css`
    border: ${$borderStyle};
    border-radius: calc(${themeGet("radii.2")} * 2) !important;
    ${getVisibilityStyles(props)}

    position: relative;
    display: flex;
    flex-direction: column;
    align-items: ${isSubCollection ? "flex-start" : "center"};
    pointer-events: none;
    ${$wideMemberGrid &&
    css`
      min-width: min(920px, calc(100vw - 200px));
    `}
  `;
});

const CREATE_NODE_TYPE_COMPONENTS = {
  [NODE_TYPES.CREATE_PERSON_NODE]: CreatePersonNode,
};

const CollectionNode = ({ data, isSubCollection, ...props }) => {
  const COLLECTION_TYPES = useMemo(
    () => ({
      [NODE_TYPES.PERSON_NODE]: PersonNode,
      [NODE_TYPES.GROUP_NODE]: GroupNode,
      [NODE_TYPES.COLLECTION_NODE]: CollectionNode,
    }),
    []
  );

  const {
    label,
    displayMode,
    creatableNodeTypes = [],
    contextForCreation = {},
    items: itemsRaw = [],
    itemsLoading,
  } = data;

  const items = useMemo(() => {
    // items + loading items
    return [
      ...itemsRaw,
      ...times(itemsLoading, (i) => ({
        id: `loading-item-${i}`,
        groupId: data.groupId,
        type: data.loadingType,
        data: { name: "Loading", isLoading: true },
      })),
    ];
  }, [data.groupId, data.loadingType, itemsLoading, itemsRaw]);

  const [showCreateNodeType, setShowCreateNodeType] = useState();
  const [labelRects, setLabelRects] = useState();
  const [highlightedNodeId, setHighlightedNodeId] = useState();
  const ref = useRef();
  const orgChartContext = useOrgChartContext();
  const currentViewTeamId = orgChartContext?.state?.currentViewTeam?.id;

  const [colCount, rowCount] = useMemo(
    () =>
      getCollectionGridDimensions({
        itemsCount: items?.length ?? 0,
        displayMode,
        memberGridLayout: data.memberGridLayout,
        isSubCollection,
      }),
    [data.memberGridLayout, displayMode, items.length, isSubCollection]
  );

  useEffect(() => {
    if (highlightedNodeId) {
      const timeout = setTimeout(() => {
        setHighlightedNodeId(null);
      }, HIGHLIGHTED_ANIMATION.DURATION * HIGHLIGHTED_ANIMATION.REPETITIONS);

      return () => clearTimeout(timeout);
    }

    return () => null;
  });

  const nodeElements = useMemo(() => {
    const showCollection =
      !!data.itemsCount || !!data.itemsMax || !!data.items?.length;

    if (showCollection) {
      return map(items, (item) => {
        const NodeComponent = COLLECTION_TYPES[item.type];

        return (
          <GridItem key={item.id} minWidth="fit-content">
            <NodeComponent
              className={item.id === highlightedNodeId && "highlighted"}
              data={item.data}
              overrideHidden
              collectionGroupId={data.groupId}
              pinnable
              isSubCollection={
                data.displayMode ===
                COLLECTION_NODE_DISPLAY_MODE.SUB_COLLECTIONS
              }
            />
          </GridItem>
        );
      });
    }

    return null;
  }, [
    data.itemsCount,
    data.itemsMax,
    data.items?.length,
    data.groupId,
    data.displayMode,
    items,
    COLLECTION_TYPES,
    highlightedNodeId,
  ]);

  const createNodeElement = useMemo(() => {
    return creatableNodeTypes.map((createableNodeType) => {
      const { type } = createableNodeType;
      const Component = CREATE_NODE_TYPE_COMPONENTS[type];

      return (
        showCreateNodeType === type && (
          <GridItem key={type} minWidth="fit-content">
            <Component
              contextForCreation={contextForCreation}
              items={items}
              onCreate={(nodeIdToHighlight) => {
                setShowCreateNodeType(null);
                setHighlightedNodeId(nodeIdToHighlight);
              }}
              onCancel={() => setShowCreateNodeType(null)}
            />
          </GridItem>
        )
      );
    });
  }, [creatableNodeTypes, contextForCreation, items, showCreateNodeType]);

  const plusOnClick = useCallback(() => {
    return setShowCreateNodeType(creatableNodeTypes[0].type);
  }, [creatableNodeTypes]);

  useEffect(() => {
    if (!ref.current) {
      return () => null;
    }
    const updateRectsAfterTimeout = (timeout = 500) => {
      setTimeout(() => {
        setLabelRects(
          (prevRects) => ref.current?.getBoundingClientRect?.() ?? prevRects
        );
      }, timeout);
    };

    const resizeObserver = new ResizeObserver(() => {
      // HACK: This ensures the label is positioned correctly after initial layout and size is properly calculated.
      [50, 250, 500, 1000].forEach((timeout) => {
        updateRectsAfterTimeout(timeout);
      });
    });

    resizeObserver.observe(ref.current);

    return () => resizeObserver.disconnect(); // clean up
  }, []);

  const loadMoreEle = useMemo(() => {
    const isMemberCollection = data.loadingType === NODE_TYPES.PERSON_NODE;
    const isTeamCollection = data.loadingType === NODE_TYPES.GROUP_NODE;
    // Direct reports collection: PERSON_NODE items without groupId/teamId
    const isDirectReportsCollection =
      !data.groupId &&
      !data.teamId &&
      items.length > 0 &&
      items[0]?.type === NODE_TYPES.PERSON_NODE;
    const itemsRemaining = (data.itemsMax ?? 0) - (data.itemsCount ?? 0);
    // hacky - in theory future view modes could use this too
    const isSupplyOrDemandCollection = data.displayMode === "single-column";
    const loadMoreCount = Math.min(itemsRemaining, MAX_ITEMS_PER_PAGE);

    const getContent = (typeName, hideLoadedTextWhenGrouping, eventData) => {
      if (itemsRemaining > 0) {
        return (
          <PositionedLoadButton
            small
            variant="ghost"
            onClick={() => {
              EventBus.emit(...eventData);
            }}
            disabled={itemsLoading > 0}
          >
            Click to load {loadMoreCount} more
          </PositionedLoadButton>
        );
      }

      if (hideLoadedTextWhenGrouping && isSupplyOrDemandCollection) {
        return null;
      }

      return (
        <PositionedLoadText color="greyDark">
          All {data.itemsCount} {typeName} loaded
        </PositionedLoadText>
      );
    };

    if (isMemberCollection) {
      return getContent("members", true, [
        EVENTS.LOAD_MORE_MEMBERS,
        data.groupId,
        data.itemsCount,
      ]);
    }

    if (isTeamCollection) {
      return getContent("teams", false, [
        EVENTS.LOAD_MORE_TEAMS,
        data.teamId,
        data.itemsCount,
      ]);
    }

    if (isDirectReportsCollection) {
      const loadMoreTeamId = data.teamId ?? data.groupId ?? currentViewTeamId;

      if (!loadMoreTeamId) {
        return null;
      }

      return getContent("direct reports", true, [
        EVENTS.LOAD_MORE_MEMBERS,
        loadMoreTeamId,
        data.itemsCount,
      ]);
    }

    return null;
  }, [
    data.displayMode,
    data.groupId,
    data.itemsCount,
    data.itemsMax,
    data.loadingType,
    data.teamId,
    items,
    itemsLoading,
    currentViewTeamId,
  ]);

  // generally need more bot padding for load more ele, but not if:
  // - there's no ele (supply/deman grouping), in which case we use uniform padding
  // - there's an ele but no items (empty collection), in which case minWidth is used
  const extraPaddingForLoadMoreEle = items.length > 0 && loadMoreEle !== null;

  const wideMemberGrid =
    data.memberGridLayout === "spacious" &&
    data.loadingType === NODE_TYPES.PERSON_NODE &&
    items.length >= MIN_MEMBER_ITEMS_FOR_WIDE_GRID;

  return (
    <StyledBox
      {...props}
      data-testid="collection-node"
      padding="l"
      $hidden={data?.hidden}
      $labelRects={labelRects}
      $borderStyle={data.borderStyle}
      $wideMemberGrid={wideMemberGrid}
      isSubCollection={isSubCollection}
      pb={extraPaddingForLoadMoreEle ? "55px" : "l"}
    >
      {label && (
        <StyledLabel
          ref={ref}
          $labelRects={labelRects}
          px="s"
          className="label"
          $labelColor={data.labelColor}
          fontSize="1.4rem"
        >
          {label}
        </StyledLabel>
      )}
      <TargetHandle $hidden type="target" position="top" />

      <Grid
        gridGap={
          data.displayMode === COLLECTION_NODE_DISPLAY_MODE.SUB_COLLECTIONS
            ? "xxl"
            : "l"
        }
        gridTemplateColumns={`repeat(${colCount}, 1fr)`}
        gridTemplateRows={`repeat(${rowCount}, auto, 1fr)`}
        columns={colCount}
        rows={rowCount}
        minWidth="150px"
        minHeight="22px"
      >
        {nodeElements}
        {createNodeElement}
      </Grid>

      {/* Left it open to create multiple node types in one collection  */}
      {!!creatableNodeTypes?.length && creatableNodeTypes[0]?.canCreate && (
        <PositionedPlusButton
          small
          disabled={showCreateNodeType}
          onClick={plusOnClick}
          variant="primary"
          icon={["fas", "plus"]}
        />
      )}
      {loadMoreEle}
    </StyledBox>
  );
};

CollectionNode.propTypes = {
  data: collectionNodeDataPropType,
  isSubCollection: PropTypes.bool,
};

export default CollectionNode;
